import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { api } from "@lr-notitia/notitia-alternis/client";

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
	if (!audioContext) {
		audioContext = new AudioContext();
	}
	return audioContext;
}

// Resume AudioContext on first user interaction (required by iOS Safari)
if (typeof document !== "undefined") {
	const unlock = () => {
		const ctx = getAudioContext();
		if (ctx.state === "running") {
			document.removeEventListener("touchstart", unlock, true);
			document.removeEventListener("touchend", unlock, true);
			document.removeEventListener("click", unlock, true);
			return;
		}
		ctx.resume().then(() => {
			document.removeEventListener("touchstart", unlock, true);
			document.removeEventListener("touchend", unlock, true);
			document.removeEventListener("click", unlock, true);
		});
	};
	document.addEventListener("touchstart", unlock, true);
	document.addEventListener("touchend", unlock, true);
	document.addEventListener("click", unlock, true);
}

type ActiveSource = {
	source: AudioBufferSourceNode;
	gain: GainNode;
};

export const useAudioPlayerStore = defineStore("alternis-audio-player", () => {
	const playing = ref(false);
	const activeSources = new Map<string, ActiveSource>();
	// Kept for external components that check .has(audioId) to determine playing state
	const audioInstances = ref<Map<string, true>>(new Map());
	const queue = ref<{ audioId: string; volume: number }[]>([]);

	function addToQueue(audioId: string, volume: number) {
		if (!queue.value.find((item) => item.audioId === audioId)) queue.value.push({ audioId, volume });
	}
	watch(queue.value, () => {
		if (stoppedByKey.value) return (stoppedByKey.value = false);
		if (queue.value.length > 0 && !playing.value) {
			play(queue.value[0].audioId, queue.value[0].volume, () => {
				queue.value.shift();
			});
		}
	});

	function play(audioId: string, volume: number, onEnded?: () => void): Promise<void> {
		return new Promise((resolve, reject) => {
			api()
				.audio()
				.get(audioId)
				.then(async (response: Blob) => {
					const ctx = getAudioContext();
					if (ctx.state !== "running") await ctx.resume();

					const arrayBuffer = await response.arrayBuffer();
					const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

					const source = ctx.createBufferSource();
					source.buffer = audioBuffer;

					const gain = ctx.createGain();
					gain.gain.value = volume;

					source.connect(gain);
					gain.connect(ctx.destination);

					activeSources.set(audioId, { source, gain });
					audioInstances.value.set(audioId, true);
					source.start();
					playing.value = true;
					resolve();

					source.onended = () => {
						playing.value = false;
						activeSources.delete(audioId);
						audioInstances.value.delete(audioId);
						if (onEnded) onEnded();
					};
				})
				.catch(() => reject());
		});
	}
	function stop(audioId: string) {
		const entry = activeSources.get(audioId);
		if (!entry) return;
		entry.source.stop();
		playing.value = false;
		activeSources.delete(audioId);
		audioInstances.value.delete(audioId);
		queue.value.shift();
	}
	function toggle(audioId: string) {
		if (playing.value) {
			stop(audioId);
		} else {
			play(audioId, 1);
		}
	}

	const stoppedByKey = ref(false);
	window.addEventListener("keydown", (e) => {
		if (e.key === "Alt" || e.key === "Escape") {
			if (!playing.value) return;
			const audioId = queue.value[0]?.audioId;
			if (audioId) {
				stop(audioId);
				stoppedByKey.value = true;
				queue.value.shift();
			}
		}
	});

	return {
		audioInstances,
		playing,
		play,
		stop,
		toggle,
		addToQueue,
	};
});
